import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useConflictsStore } from '@/stores/conflicts';
import { useUiStore } from '@/stores/ui';
import { useIsMobile } from '@/hooks/use-is-mobile';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Loader2, GitMerge } from 'lucide-react';
import { ConflictHunk, ConflictResolution } from '@/core/state-shape';
import { Textarea } from '@/components/ui/textarea';

// Mock content for hunks since it's not in the state shape
const MOCK_HUNK_CONTENT = {
    base: `function resolveThing() {\n  // initial implementation\n}`,
    ours: `function resolveThing() {\n  // my awesome implementation\n}`,
    proposed: `function resolveThing() {\n  // AI proposed implementation\n}`,
};

const HunkResolver = ({ hunk, decision, onResolve }: { hunk: ConflictHunk; decision: string | undefined; onResolve: (choice: ConflictResolution, manualText?: string) => void; }) => {
    const isMobile = useIsMobile();
    const isResolved = !!decision;
    const [isEditing, setIsEditing] = useState(false);
    const [manualText, setManualText] = useState('');

    const handleManualEdit = () => {
        setManualText(`<<<<<<< ours\n${MOCK_HUNK_CONTENT.ours}\n=======\n${MOCK_HUNK_CONTENT.proposed}\n>>>>>>> theirs`);
        setIsEditing(true);
    };

    const handleCancelEdit = () => {
        setIsEditing(false);
        setManualText('');
    };

    const handleSaveEdit = () => {
        onResolve('manual', manualText);
        setIsEditing(false);
    };

    if (isEditing) {
        return (
            <Card>
                <CardHeader>
                    <CardTitle className="text-base font-mono flex justify-between">
                        {hunk.filePath}:{hunk.base.start} (Manual Edit)
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <Textarea
                        value={manualText}
                        onChange={(e) => setManualText(e.target.value)}
                        className="font-mono h-72"
                        aria-label="Manual conflict resolution editor"
                    />
                </CardContent>
                <CardFooter className="flex justify-end gap-2">
                    <Button variant="ghost" onClick={handleCancelEdit}>Cancel</Button>
                    <Button onClick={handleSaveEdit}>Save Manual Edit</Button>
                </CardFooter>
            </Card>
        );
    }
    
    if (isMobile) {
        return (
            <Card className={isResolved ? 'opacity-60' : ''}>
                <CardHeader>
                    <CardTitle className="text-base font-mono flex justify-between">
                        {hunk.filePath}:{hunk.base.start}
                        {isResolved && <Badge variant="secondary" className="capitalize">{decision}</Badge>}
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <Tabs defaultValue="ours">
                        <TabsList className="grid w-full grid-cols-3">
                            <TabsTrigger value="ours">Ours</TabsTrigger>
                            <TabsTrigger value="base">Base</TabsTrigger>
                            <TabsTrigger value="proposed">Proposed</TabsTrigger>
                        </TabsList>
                        <TabsContent value="ours"><pre className="bg-muted p-2 rounded-md mt-2 text-xs overflow-x-auto">{MOCK_HUNK_CONTENT.ours}</pre></TabsContent>
                        <TabsContent value="base"><pre className="bg-muted p-2 rounded-md mt-2 text-xs overflow-x-auto">{MOCK_HUNK_CONTENT.base}</pre></TabsContent>
                        <TabsContent value="proposed"><pre className="bg-muted p-2 rounded-md mt-2 text-xs overflow-x-auto">{MOCK_HUNK_CONTENT.proposed}</pre></TabsContent>
                    </Tabs>
                </CardContent>
                <CardFooter className="flex justify-end gap-2 flex-wrap">
                    <Button variant="outline" size="sm" onClick={() => onResolve('ours')} disabled={isResolved}>Accept Ours</Button>
                    <Button variant="outline" size="sm" onClick={() => onResolve('proposed')} disabled={isResolved}>Accept Theirs</Button>
                    <Button size="sm" onClick={() => onResolve('both')} disabled={isResolved}>Use Both</Button>
                    <Button variant="secondary" size="sm" onClick={handleManualEdit} disabled={isResolved}>Manual Edit</Button>
                </CardFooter>
            </Card>
        );
    }

    return (
        <Card className={isResolved ? 'opacity-60' : ''}>
            <CardHeader>
                <CardTitle className="text-base font-mono flex justify-between">
                    {hunk.filePath}:{hunk.base.start}
                    {isResolved && <Badge variant="secondary" className="capitalize">{decision}</Badge>}
                </CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-3 gap-4">
                <div>
                    <h3 className="font-semibold mb-2 text-center">Ours</h3>
                    <pre className="bg-muted p-2 rounded-md text-xs overflow-x-auto h-full">{MOCK_HUNK_CONTENT.ours}</pre>
                </div>
                <div>
                    <h3 className="font-semibold mb-2 text-center">Base</h3>
                    <pre className="bg-muted p-2 rounded-md text-xs overflow-x-auto h-full">{MOCK_HUNK_CONTENT.base}</pre>
                </div>
                <div>
                    <h3 className="font-semibold mb-2 text-center">Proposed (Theirs)</h3>
                    <pre className="bg-muted p-2 rounded-md text-xs overflow-x-auto h-full">{MOCK_HUNK_CONTENT.proposed}</pre>
                </div>
            </CardContent>
            <CardFooter className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => onResolve('ours')} disabled={isResolved}>Accept Ours</Button>
                <Button variant="outline" onClick={() => onResolve('proposed')} disabled={isResolved}>Accept Theirs</Button>
                <Button onClick={() => onResolve('both')} disabled={isResolved}>Use Both</Button>
                <Button variant="secondary" onClick={handleManualEdit} disabled={isResolved}>Manual Edit</Button>
            </CardFooter>
        </Card>
    );
};

export default function ConflictsPage() {
    // Assuming the router will be updated to include `:sessionId`
    const { sessionId } = useParams<{ sessionId: string }>();
    const setHeaderTitle = useUiStore((state) => state.setHeaderTitle);
    const { conflicts, load: loadConflicts, resolveHunk } = useConflictsStore();
    const [loading, setLoading] = useState(true);
    const isMobile = useIsMobile();

    useEffect(() => {
        setHeaderTitle('Resolve Conflicts');
    }, [setHeaderTitle]);

    useEffect(() => {
        if (sessionId) {
            setLoading(true);
            loadConflicts(sessionId).finally(() => setLoading(false));
        }
    }, [sessionId, loadConflicts]);

    const hunks = useMemo(() => {
        if (!conflicts) return [];
        return Object.values(conflicts.hunks);
    }, [conflicts]);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-full p-8">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                <p className="ml-2">Loading conflict session...</p>
            </div>
        );
    }
    
    if (!conflicts) {
        return <div className="p-8 text-center text-muted-foreground">Could not load conflict session for ID: {sessionId}</div>;
    }
    
    const unresolvedCount = hunks.filter(h => !conflicts.decisions[h.id] || conflicts.decisions[h.id].choice === 'unset').length;
    return (
        <div className="container mx-auto p-4 lg:p-6 space-y-6">
            <header className="space-y-2">
                <h1 className="text-2xl font-bold items-center gap-2 hidden md:flex">
                    <GitMerge className="h-6 w-6" />
                    Resolve Conflicts
                </h1>

                <p className="text-muted-foreground hidden md:flex">
                    Resolve conflicts for task: <span className="font-semibold text-foreground">{conflicts.taskId || 'Unknown'}</span>
                </p>
                <Card>
                    <CardHeader>
                        <CardTitle>Resolution Progress</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <p>{unresolvedCount} of {hunks.length} conflicts remaining.</p>
                    </CardContent>
                </Card>
            </header>

            <div className="space-y-4">
                {hunks.map(hunk => (
                    <HunkResolver 
                        key={hunk.id} 
                        hunk={hunk} 
                        decision={conflicts.decisions[hunk.id]?.choice}
                        onResolve={(choice, manualText) => resolveHunk(hunk.id, choice, manualText)}
                    />
                ))}
            </div>
            
            <footer className="flex justify-end pt-4">
                <Button size="lg" disabled={unresolvedCount > 0}>Apply Decisions</Button>
            </footer>
        </div>
    );
}